import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, FileText, Workflow, Image, Globe,
  BarChart3, Radio, Bot, Brain, Settings,
  ChevronLeft, ChevronRight, LogOut, Zap, Users, Rss, Cpu,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { cn } from '../../lib/utils';
import api from '../../lib/api';


export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const { user, logout } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (user?.role !== 'admin') return;
    const fetch = async () => {
      try {
        const res = await api.get('/users/pending/count');
        setPendingCount(res.data.count || 0);
      } catch { }
    };
    fetch();
    const interval = setInterval(fetch, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const isAdmin  = user?.role === 'admin';
  const isEditor = user?.role === 'editor';

  const navigation = [
    { name: 'Dashboard',             href: '/',             icon: LayoutDashboard },
    { name: 'Newsroom',               href: '/stories',      icon: FileText },
    ...(isAdmin ? [
      { name: 'EventFlow',           href: '/feeds',         icon: Workflow },
      { name: 'Manage sources',      href: '/feeds/sources', icon: Rss },
    ] : []),
    ...(isAdmin || isEditor ? [
      { name: 'Publications',        href: '/feeds/publications', icon: Globe },
    ] : []),
    { name: 'Media Library',         href: '/media',        icon: Image },
    ...(isAdmin ? [
      { name: 'AI Inference',        href: '/inference',    icon: Cpu },
    ] : []),
    { name: 'Analytics & Reports',   href: '/analytics',    icon: BarChart3 },
    { name: 'Proprietary Audiences', href: '/audiences',    icon: Radio },
    ...(isAdmin ? [
      { name: 'Automated Stories',   href: '/automated',    icon: Bot },
      { name: 'Data Intelligence',   href: '/intelligence', icon: Brain },
    ] : []),
    { name: 'Settings',              href: '/settings',     icon: Settings },
    ...(isAdmin ? [
      { name: 'Users Management',    href: '/users',        icon: Users, badge: pendingCount },
    ] : []),
  ];

  const activeHref = navigation.reduce((best, item) => {
    const path = location.pathname;
    const matches = item.href === '/'
      ? path === '/'
      : path === item.href || path.startsWith(item.href + '/');
    return matches && item.href.length > best.length ? item.href : best;
  }, '');

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 h-screen bg-[var(--color-surface-sidebar)] border-r border-white/10 flex flex-col transition-all duration-200 z-20',
        collapsed ? 'w-16' : 'w-64'
      )}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="p-4 border-b border-white/10 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
          <Zap className="w-5 h-5 text-white" />
        </div>
        {!collapsed && (
          <div>
            <h1 className="font-heading font-bold text-lg text-white tracking-tight">OKEBOURNE</h1>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Command Centre</p>
          </div>
        )}
      </div>

      <nav className="flex-1 py-4 px-2 overflow-y-auto">
        {navigation.map((item) => {
          const isActive = item.href === activeHref;

          return (
            <NavLink
              key={item.name}
              to={item.href}
              data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 mb-1 rounded-lg transition-all duration-150',
                isActive
                  ? 'bg-white/15 text-white'
                  : 'text-slate-300 hover:bg-white/10 hover:text-white'
              )}
            >
              <item.icon className={cn('w-5 h-5 flex-shrink-0', isActive ? 'text-white' : 'text-slate-400')} />
              {!collapsed && (
                <span className="text-sm font-medium flex-1">{item.name}</span>
              )}
              {!collapsed && item.badge > 0 && (
                <span className="bg-amber-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                  {item.badge}
                </span>
              )}
              {collapsed && item.badge > 0 && (
                <span className="absolute left-7 top-1 w-2 h-2 bg-amber-500 rounded-full" />
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-3 border-t border-white/10">
        <div className={cn('flex items-center', collapsed ? 'justify-center' : 'gap-3')}>
          <Avatar className="w-9 h-9">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback className="bg-blue-500 text-white text-sm">
              {user?.name?.charAt(0) || 'A'}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name || 'Admin'}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email || ''}</p>
            </div>
          )}
          {!collapsed && (
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              className="text-slate-400 hover:text-white hover:bg-white/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <Button
        variant="ghost"
        size="icon"
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-white border border-slate-200 shadow-sm hover:bg-slate-50"
        data-testid="sidebar-toggle"
      >
        {collapsed ? (
          <ChevronRight className="w-3 h-3 text-slate-600" />
        ) : (
          <ChevronLeft className="w-3 h-3 text-slate-600" />
        )}
      </Button>
    </aside>
  );
}

export default Sidebar;